import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import './PublicPersonaChat.css';

const PublicPersonaChat = () => {
  const { token } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [persona, setPersona] = useState(null);
  const [clientName, setClientName] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [showEndModal, setShowEndModal] = useState(false);
  const [hasVoice, setHasVoice] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);
  const [useCase, setUseCase] = useState('coaching');
  
  // Voice input states
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  
  // Session timer states
  const [sessionDuration, setSessionDuration] = useState(null); // in minutes
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null); // in seconds
  const [showTimer, setShowTimer] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');
  const [lastConversation, setLastConversation] = useState(null);
  const [userUsedVoice, setUserUsedVoice] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);

  useEffect(() => {
    validateToken();
  }, [token]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
    };
  }, []);

  // Session timer effect
  useEffect(() => {
    if (!sessionDuration || !sessionStartTime || sessionExpired || conversationEnded) return;
    
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - sessionStartTime) / 1000);
      const totalSeconds = sessionDuration * 60;
      const remaining = totalSeconds - elapsed;
      
      setTimeRemaining(remaining);
      
      // Show timer when 2 minutes (120 seconds) or less remaining
      if (remaining <= 120 && remaining > 0) {
        setShowTimer(true);
      }
      
      // Session expired
      if (remaining <= 0) {
        setSessionExpired(true);
        setShowTimer(false);
        clearInterval(interval);
      }
    }, 1000);
    
    return () => clearInterval(interval);
  }, [sessionDuration, sessionStartTime, sessionExpired, conversationEnded]);

  const formatTimeRemaining = (seconds) => {
    if (seconds <= 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const validateToken = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/persona/public/${token}`
      );
      if (response.data.valid) {
        setPersona(response.data.persona);
        setClientName(response.data.client.firstName);
        setHasVoice(response.data.hasVoice || false);
        setUseCase(response.data.persona.useCase || 'coaching');
// Check for resumable conversation
        try {
          const lastConvRes = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/persona/public/${token}/last-conversation`
          );
          if (lastConvRes.data.hasConversation) {
            setLastConversation(lastConvRes.data);
          }
        } catch (e) {
          console.log('No previous conversation');
        }
        
        setLoading(false);
      } else {
        setError(response.data.error || 'Invalid link');
        setLoading(false);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to access this link');
      setLoading(false);
    }
  };

  const startConversation = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/persona/public/${token}/start`
      );
      setConversationId(response.data.conversationId);
      setMessages([{
        role: 'persona',
        content: response.data.greeting,
        timestamp: new Date()
      }]);
      
      // Set session timer if duration provided
      if (response.data.sessionDuration) {
        setSessionDuration(response.data.sessionDuration);
        setSessionStartTime(Date.now());
      }
      
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      // Check if it's a frequency limit error
      if (err.response?.data?.limitReached) {
        setLimitReached(true);
        setLimitMessage(err.response?.data?.error || 'Session limit reached. Please try again later.');
        setLoading(false);
      } else {
        setError(err.response?.data?.error || 'Unable to start conversation');
        setLoading(false);
      }
    }
  };

  const resumeConversation = async () => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/persona/public/${token}/resume/${lastConversation.conversationId}`
      );
      setConversationId(response.data.conversationId);
      setMessages(response.data.messages.map(m => ({
        role: m.role,
        content: m.content,
        timestamp: m.timestamp
      })));
      setLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      setError(err.response?.data?.error || 'Unable to resume conversation');
      setLoading(false);
    }
  };

    const playAudio = (audioBase64) => {
    if (!audioEnabled || !audioBase64 || !userUsedVoice) return;
    try {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      const audio = new Audio(`data:audio/mpeg;base64,${audioBase64}`);
      audioRef.current = audio;
      audio.play().catch(err => console.log('Audio play failed:', err));
    } catch (err) {
      console.error('Error playing audio:', err);
    }
  };

  const sendMessage = async (messageText) => {
    const userMessage = messageText || inputMessage.trim();
    if (!userMessage || sending) return;

    setInputMessage('');
    setSending(true);

    setMessages(prev => [...prev, {
      role: 'client',
      content: userMessage,
      timestamp: new Date()
    }]);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/persona/public/conversation/${conversationId}/message`,
        { message: userMessage }
      );

      setMessages(prev => [...prev, {
        role: 'persona',
        content: response.data.response,
        timestamp: new Date(),
        hasAudio: !!response.data.audio
      }]);

      if (response.data.audio) {
        playAudio(response.data.audio);
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'system',
        content: 'Sorry, there was an error. Please try again.',
        timestamp: new Date()
      }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  // Voice recording functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
        
        // Process the recording
        const audioBlob = new Blob(audioChunksRef.current, { 
          type: mediaRecorder.mimeType 
        });
        
        await transcribeAudio(audioBlob);
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (err) {
      console.error('Error starting recording:', err);
      alert('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }
    
    setIsRecording(false);
  };

  const transcribeAudio = async (audioBlob) => {
    setTranscribing(true);
    
    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'recording.webm');

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/transcribe`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.text && response.data.text.trim()) {
        setUserUsedVoice(true);
        await sendMessage(response.data.text.trim());
      } else {
        alert('Could not understand audio. Please try again or type your message.');
      }
    } catch (err) {
      console.error('Transcription error:', err);
      alert('Error transcribing audio. Please try again or type your message.');
    } finally {
      setTranscribing(false);
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const endConversation = async () => {
    try {
      setSending(true);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/persona/public/conversation/${conversationId}/end`
      );
      setTranscript(response.data.transcript);
      setConversationEnded(true);
      setShowEndModal(true);
    } catch (err) {
      setError('Unable to end conversation properly');
    } finally {
      setSending(false);
    }
  };

  const downloadTranscript = () => {
    const blob = new Blob([transcript], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `conversation-${new Date().toISOString().split('T')[0]}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading && !persona) {
    return (
      <div className="public-chat-container">
        <div className="loading-screen">
          <div className="loading-spinner-large"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="public-chat-container">
        <div className="error-screen">
          <div className="error-icon">😕</div>
          <h2>Unable to Access</h2>
          <p>{error}</p>
          <p className="error-hint">Please contact your {useCase === 'sales' ? 'sales rep' : useCase === 'support' ? 'support team' : useCase === 'training' ? 'trainer' : useCase === 'consulting' ? 'consultant' : 'coach'} for a new link.</p>
        </div>
      </div>
    );
  }

  if (limitReached) {
    return (
      <div className="public-chat-container">
        <div className="limit-screen">
          {persona?.profilePhotoUrl && (
            <div className="coach-photo">
              <img src={persona.profilePhotoUrl} alt={persona?.displayName} />
            </div>
          )}
          <div className="limit-icon">⏰</div>
          <h2>Session Limit Reached</h2>
          <p>{limitMessage}</p>
          <p className="limit-hint">Your {useCase === 'sales' ? 'sales rep' : useCase === 'support' ? 'support team' : useCase === 'training' ? 'trainer' : useCase === 'consulting' ? 'consultant' : 'coach'} has set a limit on how often you can chat with the AI assistant.</p>
        </div>
      </div>
    );
  }

  if (!conversationId) {
    return (
      <div className="public-chat-container">
        <div className="welcome-screen">
          {persona.profilePhotoUrl && (
            <div className="coach-photo">
              <img src={persona.profilePhotoUrl} alt={persona.displayName} />
            </div>
          )}
          <h1>{persona.displayName}</h1>
          <p className="coach-name">AI Assistant for {persona.coachName}</p>
          
          <div className="welcome-message">
            <p>Hi {clientName}! 👋</p>
            <p>{
              useCase === 'sales' ? "I'm here to help answer your questions and discuss how we can best serve your needs." :
              useCase === 'support' ? "I'm here to help resolve any issues or answer questions you might have." :
              useCase === 'training' ? "I'm here to help you learn and answer any questions about the material." :
              useCase === 'consulting' ? "I'm here to help answer your questions and provide guidance." :
              "I'm here to help you between coaching sessions. Feel free to share what's on your mind, check in on your goals, or ask any questions."
            }</p>
          </div>

          <div className="transparency-notice">
            <span>🤖</span>
            <p>This is an AI assistant. Your {useCase === 'sales' ? 'sales rep' : useCase === 'support' ? 'support team' : useCase === 'training' ? 'trainer' : useCase === 'consulting' ? 'consultant' : 'coach'} will receive a summary of our conversation.</p>
          </div>

          {hasVoice && (
            <div className="voice-notice">
              <span>🎙️</span>
              <p>This AI speaks with your {useCase === 'sales' ? "rep's" : useCase === 'support' ? "team's" : useCase === 'training' ? "trainer's" : useCase === 'consulting' ? "consultant's" : "coach's"} voice!</p>
            </div>
          )}

    {lastConversation && (
            <button className="start-btn" onClick={resumeConversation} disabled={loading} style={{ marginBottom: '0.5rem' }}>
              {loading ? 'Resuming...' : `Continue Previous Session (${lastConversation.messageCount} messages)`}
            </button>
          )}
          
          <button className="start-btn" onClick={startConversation} disabled={loading} style={lastConversation ? { background: '#6c757d' } : {}}>
            {loading ? 'Starting...' : 'Start New Conversation'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="public-chat-container">
      {/* Session Timer */}
      {showTimer && !sessionExpired && !conversationEnded && (
        <div className={`session-timer ${timeRemaining <= 60 ? 'urgent' : ''}`}>
          <span className="timer-icon">⏱️</span>
          <span className="timer-text">{formatTimeRemaining(timeRemaining)} remaining</span>
        </div>
      )}

      {/* Session Expired Overlay */}
      {sessionExpired && !conversationEnded && (
        <div className="session-expired-overlay">
          <div className="session-expired-content">
            <span className="expired-icon">⏰</span>
            <h2>Session Complete</h2>
            <p>Your session time has ended. Thank you for chatting!</p>
            <p className="expired-note">Your {useCase === 'sales' ? 'sales rep' : useCase === 'support' ? 'support team' : useCase === 'training' ? 'trainer' : useCase === 'consulting' ? 'consultant' : 'coach'} will review this conversation.</p>
          </div>
        </div>
      )}

      <div className="chat-header">
        <div className="header-info">
          {persona.profilePhotoUrl && (
            <img src={persona.profilePhotoUrl} alt={persona.displayName} className="header-photo" />
          )}
          <div className="header-text">
            <h2>{persona.displayName}</h2>
            <span className="ai-badge">🤖 AI Assistant</span>
          </div>
        </div>
        <div className="header-actions">
          {hasVoice && (
            <button 
              className={`audio-toggle ${audioEnabled ? 'on' : 'off'}`}
              onClick={() => setAudioEnabled(!audioEnabled)}
              title={audioEnabled ? 'Mute audio' : 'Enable audio'}
            >
              {audioEnabled ? '🔊' : '🔇'}
            </button>
          )}
          {!conversationEnded && !sessionExpired && (
            <button className="end-btn" onClick={() => setShowEndModal(true)}>
              End Chat
            </button>
          )}
        </div>
      </div>

      <div className="chat-messages">
        {messages.map((msg, index) => (
          <div key={index} className={`message ${msg.role}`}>
            {msg.role === 'persona' && persona.profilePhotoUrl && (
              <img src={persona.profilePhotoUrl} alt="" className="message-avatar" />
            )}
            <div className="message-content">
              <p>{msg.content}</p>
              <span className="message-time">
                {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                {msg.hasAudio && ' 🔊'}
              </span>
            </div>
          </div>
        ))}
        
        {(sending || transcribing) && (
          <div className="message persona">
            {persona.profilePhotoUrl && (
              <img src={persona.profilePhotoUrl} alt="" className="message-avatar" />
            )}
            <div className="message-content typing">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      {!conversationEnded && (
        <div className="chat-input-container">
          {isRecording ? (
            <div className="recording-indicator">
              <div className="recording-pulse"></div>
              <span className="recording-time">{formatRecordingTime(recordingTime)}</span>
              <button 
                type="button" 
                className="stop-recording-btn"
                onClick={stopRecording}
              >
                ⏹️ Stop & Send
              </button>
            </div>
          ) : transcribing ? (
            <div className="transcribing-indicator">
              <div className="transcribing-spinner"></div>
              <span>Transcribing...</span>
            </div>
          ) : (
            <form className="chat-input-form" onSubmit={handleFormSubmit}>
              <button 
                type="button" 
                className="mic-btn"
                onClick={startRecording}
                disabled={sending || sessionExpired}
                title="Hold to record voice message"
              >
                🎤
              </button>
              <input
                ref={inputRef}
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                placeholder={sessionExpired ? "Session ended" : "Type or tap 🎤 to speak..."}
                disabled={sending || sessionExpired}
              />
              <button type="submit" disabled={!inputMessage.trim() || sending || sessionExpired}>
                {sending ? '...' : '➤'}
              </button>
            </form>
          )}
        </div>
      )}

      {conversationEnded && !showEndModal && (
        <div className="conversation-ended-banner">
          <p>Conversation ended. Thank you for chatting!</p>
          <button onClick={downloadTranscript}>📥 Download Transcript</button>
        </div>
      )}

      {showEndModal && (
        <div className="modal-overlay" onClick={() => !conversationEnded && setShowEndModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            {!conversationEnded ? (
              <>
                <h2>End Conversation?</h2>
                <p>Your {useCase === 'sales' ? 'sales rep' : useCase === 'support' ? 'support team' : useCase === 'training' ? 'trainer' : useCase === 'consulting' ? 'consultant' : 'coach'} will receive a summary of this conversation.</p>
                <div className="modal-actions">
                  <button className="cancel-btn" onClick={() => setShowEndModal(false)}>
                    Continue Chatting
                  </button>
                  <button className="confirm-btn" onClick={endConversation} disabled={sending}>
                    {sending ? 'Ending...' : 'End Conversation'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div className="success-icon">✅</div>
                <h2>Conversation Complete!</h2>
                <p>Thank you for chatting! Your {useCase === 'sales' ? 'sales rep' : useCase === 'support' ? 'support team' : useCase === 'training' ? 'trainer' : useCase === 'consulting' ? 'consultant' : 'coach'} will review this conversation and follow up with you.</p>
                <div className="modal-actions">
                  <button className="download-btn" onClick={downloadTranscript}>
                    📥 Download Transcript
                  </button>
                  <button className="done-btn" onClick={() => setShowEndModal(false)}>
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PublicPersonaChat;